/** Parser e tipos para pedidos de catálogo WhatsApp (Uazapi / Cloud API). */

export interface WhatsAppCatalogOrderItem {
  name: string;
  quantity: number;
  price?: number | null;
  currency?: string;
}

export interface WhatsAppCatalogOrderDisplay {
  title?: string | null;
  itemCount: number;
  total?: number | null;
  currency: string;
  thumbnailUrl?: string | null;
  orderId?: string | null;
  items?: WhatsAppCatalogOrderItem[];
}

const BODY_ORDER_RE =
  /^🛒\s*Pedido do catálogo:\s*(.+?)\s*\((\d+)\s*(?:item|itens)\)\s*—\s*Total:\s*(\w+)\s*([\d.,]+)/i;

function parseNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value.replace(/[^\d.,-]/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseUazapiOrder(raw: Record<string, unknown>): WhatsAppCatalogOrderDisplay | null {
  const orderId = raw.orderId ?? raw.order_id;
  const title = raw.title;
  const itemCount = parseNumber(raw.itemCount ?? raw.item_count);
  const total = parseNumber(raw.total);
  const currency = String(raw.currency ?? raw.totalCurrencyCode ?? "BRL").toUpperCase();
  const thumbnailUrl =
    (typeof raw.thumbnailUrl === "string" && raw.thumbnailUrl) ||
    (typeof raw.thumbnail === "string" && raw.thumbnail) ||
    null;

  if (!orderId && itemCount == null && total == null && !title) return null;

  return {
    orderId: orderId != null ? String(orderId) : null,
    title: typeof title === "string" ? title : null,
    itemCount: itemCount ?? 1,
    total,
    currency,
    thumbnailUrl,
  };
}

function parseCloudOrder(raw: Record<string, unknown>): WhatsAppCatalogOrderDisplay | null {
  const items = Array.isArray(raw.product_items) ? raw.product_items : [];
  if (items.length === 0 && !raw.text) return null;

  let total = 0;
  let currency = "BRL";
  const parsedItems: WhatsAppCatalogOrderItem[] = [];

  for (const item of items) {
    const node = (item as Record<string, unknown>) ?? {};
    const qty = parseNumber(node.quantity) ?? 1;
    const price = parseNumber(node.item_price) ?? 0;
    const itemCurrency =
      typeof node.currency === "string" && node.currency
        ? node.currency.toUpperCase()
        : currency;
    total += qty * price;
    if (typeof node.currency === "string" && node.currency) {
      currency = node.currency.toUpperCase();
    }
    parsedItems.push({
      name: String(node.product_retailer_id ?? node.name ?? "Item"),
      quantity: qty,
      price: price > 0 ? price : null,
      currency: itemCurrency,
    });
  }

  return {
    orderId: typeof raw.catalog_id === "string" ? raw.catalog_id : null,
    title: typeof raw.text === "string" ? raw.text.trim() || null : null,
    itemCount: parsedItems.reduce((sum, i) => sum + i.quantity, 0) || items.length || 1,
    total: total > 0 ? total : null,
    currency,
    thumbnailUrl: null,
    items: parsedItems.length > 0 ? parsedItems : undefined,
  };
}

function parseBodyFallback(body: string): WhatsAppCatalogOrderDisplay | null {
  const match = body.trim().match(BODY_ORDER_RE);
  if (!match) return null;
  const [, title, countStr, currency, amountStr] = match;
  const itemCount = parseNumber(countStr) ?? 1;
  const total = parseNumber(amountStr);
  return {
    title: title.trim(),
    itemCount,
    total,
    currency: currency.toUpperCase(),
    thumbnailUrl: null,
  };
}

export function parseCatalogOrderDisplay(
  metadata: Record<string, unknown> | null | undefined,
  body?: string | null,
): WhatsAppCatalogOrderDisplay | null {
  if (!metadata && !body) return null;

  const meta = metadata ?? {};
  const type = String(meta.type ?? meta.normalized_message_subtype ?? "").toLowerCase();

  if (meta.order && typeof meta.order === "object") {
    const raw = meta.order as Record<string, unknown>;
    if (Array.isArray(raw.product_items) && raw.product_items.length > 0) {
      const cloud = parseCloudOrder(raw);
      if (cloud) return cloud;
    }
    const uazapi = parseUazapiOrder(raw);
    if (uazapi) return uazapi;
    const cloud = parseCloudOrder(raw);
    if (cloud) return cloud;
  }

  if (type === "order") {
    const productCount = parseNumber(meta.product_count);
    if (productCount != null && productCount > 0) {
      return {
        itemCount: productCount,
        currency: "BRL",
        total: null,
        title: null,
        thumbnailUrl: null,
      };
    }
  }

  if (body) {
    const fromBody = parseBodyFallback(body);
    if (fromBody) return fromBody;
  }

  return null;
}

export function formatCatalogOrderItemCount(count: number): string {
  return count === 1 ? "1 item" : `${count} itens`;
}

export function formatCatalogOrderSubtotal(
  total: number | null | undefined,
  currency: string,
): string | null {
  if (total == null || !Number.isFinite(total)) return null;
  const cur = (currency || "BRL").toUpperCase();
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: cur }).format(total);
  } catch {
    return `${cur} ${total.toFixed(2)}`;
  }
}
